import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Github, Instagram, Linkedin } from "lucide-react";


// --- Contact Page Component ---
const ContactPage = ({ theme }) => {
  // Receive theme prop
  const [formStatus, setFormStatus] = useState(""); // 'idle', 'sending', 'success', 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("sending");

    // In a real application, you would use EmailJS or a similar service here.
    // Example using a placeholder fetch:
    const serviceID = "service_ahstxa3";
    const templateID = "template_oj2w6vd";
    const publicKey = "mAKDk6CxOINYonqA1"; // This is the public key, not a secret!

    try {
      const response = await fetch(
        `https://api.emailjs.com/api/v1.0/email/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            service_id: serviceID,
            template_id: templateID,
            user_id: publicKey,
            template_params: {
              name: e.target.name.value,
              email: e.target.email.value,
              message: e.target.message.value,
              subject: e.target.subject.value
            },
          }),
        }
      );

      if (response.ok) {
        setFormStatus("success");
        e.target.reset();
      } else {
        setFormStatus("error");
        console.error("EmailJS Error:", await response.json());
      }
    } catch (error) {
      setFormStatus("error");
      console.error("Network Error:", error);
    }

    // Simulating API call for demonstration
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setFormStatus("success"); // Or 'error' for testing failure
    e.target.reset();
  };

  const socialLinks = [
    {
      icon: <Github size={24} />,
      url: "https://github.com/Ranjansharma-ind",
      label: "GitHub",
    },
    {
      icon: <Linkedin size={24} />,
      url: "https://linkedin.com/in/ranjansharma-ind",
      label: "LinkedIn",
    },
    {
      icon: <Instagram size={24} />,
      url: "https://www.instagram.com/ranjan_s.h.a.r.m.a/",
      label: "Instagram",
    }, // Twitter not in resume, keeping placeholder
  ];

  const InputField = ({ type, name, placeholder, isTextArea = false }) => {
    const [isFocused, setIsFocused] = useState(false);
    return (
      <div className="relative mb-6">
        {isTextArea ? (
          <motion.textarea
            name={name}
            placeholder={isFocused ? "" : placeholder}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => setIsFocused(e.target.value !== "" || false)}
            className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-pink-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white resize-y min-h-[120px]"
            rows="5"
            required
          />
        ) : (
          <motion.input
            type={type}
            name={name}
            placeholder={isFocused ? "" : placeholder}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => setIsFocused(e.target.value !== "" || false)}
            className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-pink-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
            required
          />
        )}
        <AnimatePresence>
          {!isFocused && (
            <motion.label
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute left-4 top-4 text-gray-500 dark:text-gray-400 pointer-events-none"
            >
              {placeholder}
            </motion.label>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const gradientBackgroundClass =
    theme === "dark"
      ? "bg-gradient-to-br from-gray-900 to-gray-800" // Black and dark cream gradient for dark mode
      : "bg-gradient-to-br from-white to-amber-50"; // White and light cream gradient for light mode

  return (
    <section
      id="contact"
      className={`min-h-screen py-20 transition-colors duration-300 ${gradientBackgroundClass}`}
    >
      <div className="container mx-auto px-4 max-w-2xl">
        <motion.h2
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-5xl font-bold text-center mb-12 text-gray dark:text-white"
        >
          Get In Touch
        </motion.h2>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl"
        >
          <InputField type="text" name="name" placeholder="Your Name" />
          <InputField type="email" name="email" placeholder="Your Email" />
          <InputField type="text" name="subject" placeholder="Subject" />
          <InputField name="message" placeholder="Your Message" isTextArea />

          <motion.button
            type="submit"
            whileHover={{
              scale: 1.05,
              boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
            }}
            whileTap={{ scale: 0.95 }}
            className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full shadow-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 text-lg"
            disabled={formStatus === "sending"}
          >
            {formStatus === "sending" ? "Sending..." : "Send Message"}
          </motion.button>

          {formStatus === "success" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 text-center text-green-600 dark:text-green-400 font-semibold"
            >
              Message sent successfully!
            </motion.div>
          )}
          {formStatus === "error" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 bg-red-100 dark:bg-red-900 rounded-lg shadow-inner border border-red-300 dark:border-red-700 text-red-800 dark:text-red-200"
            >
              <p>{formStatus}</p>
            </motion.div>
          )}
        </motion.form>

        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold mb-6 text-gray dark:text-white">
            Find Me On
          </h3>
          <div className="flex justify-center space-x-6">
            {socialLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.2, y: -5 }}
                whileTap={{ scale: 0.9 }}
                className="text-gray dark:text-gray-300 hover:text-purple-600 dark:hover:text-pink-500 transition-colors duration-300"
                aria-label={link.label}
              >
                {link.icon}
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactPage;